import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";

const NAV_LINKS = [
  { label: "Home", path: "/" },
  { label: "About", path: "/about" },
  { label: "Creators", path: "/creators" },
  { label: "Apply", path: "/apply" },
  { label: "Contact", path: "/contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  return (
    <header
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-500 ${
        scrolled ? "bg-white shadow-sm" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 flex items-center justify-between h-20">
        {/* Logo — stacked magazine mark */}
        <Link to="/" className="cursor-pointer group" aria-label="Make It A Magazine – Home">
          <div className="relative w-10 h-12">
            {/* Back magazine — fanned out */}
            <div
              className={`absolute w-7 h-9 top-0 right-0 rotate-[12deg] origin-bottom-left transition-colors duration-300 rounded-sm ${
                scrolled ? "bg-[#2B2B2B]/20" : "bg-white/25"
              }`}
            />
            {/* Front magazine — with red header band */}
            <div
              className={`absolute w-7 h-9 bottom-0 left-0 rounded-sm overflow-hidden transition-colors duration-300 ${
                scrolled ? "bg-[#1A1A1A]" : "bg-white"
              }`}
            >
              {/* Top accent band */}
              <div className="h-2.5 w-full bg-[#C41E3A]" />
              {/* Fake content lines */}
              <div className={`mx-1.5 mt-1.5 space-y-1 transition-colors duration-300`}>
                <div className={`h-0.5 w-4 rounded-full ${scrolled ? "bg-white/40" : "bg-black/20"}`} />
                <div className={`h-0.5 w-3 rounded-full ${scrolled ? "bg-white/25" : "bg-black/15"}`} />
                <div className={`h-0.5 w-4 rounded-full ${scrolled ? "bg-white/25" : "bg-black/15"}`} />
              </div>
            </div>
          </div>
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-10">
          {NAV_LINKS.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              className={`text-sm tracking-widest uppercase font-medium transition-colors duration-200 cursor-pointer whitespace-nowrap ${
                location.pathname === link.path
                  ? "text-[#C41E3A]"
                  : scrolled
                  ? "text-[#2B2B2B] hover:text-[#C41E3A]"
                  : "text-white hover:text-[#F5F1E8]"
              }`}
            >
              {link.label}
            </Link>
          ))}
        </nav>

        {/* CTA Button */}
        <Link
          to="/apply"
          className="hidden md:inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-[#C41E3A] text-white text-sm font-semibold tracking-wide whitespace-nowrap hover:bg-[#A0182E] transition-colors duration-200 cursor-pointer"
        >
          Apply Now
        </Link>

        {/* Mobile Hamburger */}
        <button
          className="md:hidden w-10 h-10 flex flex-col items-center justify-center gap-1.5 cursor-pointer"
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-label="Toggle menu"
        >
          <span className={`block h-0.5 w-6 transition-all duration-300 ${scrolled ? "bg-[#2B2B2B]" : "bg-white"} ${mobileOpen ? "rotate-45 translate-y-2" : ""}`} />
          <span className={`block h-0.5 w-6 transition-all duration-300 ${scrolled ? "bg-[#2B2B2B]" : "bg-white"} ${mobileOpen ? "opacity-0" : ""}`} />
          <span className={`block h-0.5 w-6 transition-all duration-300 ${scrolled ? "bg-[#2B2B2B]" : "bg-white"} ${mobileOpen ? "-rotate-45 -translate-y-2" : ""}`} />
        </button>
      </div>

      {/* Mobile Menu */}
      <div
        className={`md:hidden bg-white overflow-hidden transition-all duration-300 ${
          mobileOpen ? "max-h-96" : "max-h-0"
        }`}
      >
        <div className="px-6 py-6 flex flex-col gap-5">
          {NAV_LINKS.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              className={`text-sm tracking-widest uppercase font-medium cursor-pointer ${
                location.pathname === link.path ? "text-[#C41E3A]" : "text-[#2B2B2B]"
              }`}
            >
              {link.label}
            </Link>
          ))}
          <Link
            to="/apply"
            className="inline-flex items-center justify-center px-6 py-2.5 rounded-full bg-[#C41E3A] text-white text-sm font-semibold whitespace-nowrap cursor-pointer"
          >
            Apply Now
          </Link>
        </div>
      </div>
    </header>
  );
}
